import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { COUNTRY_CONFIG, getCountryCodeFromPath, getCountryConfig, normalizeCountryCode } from "../Geo/countryConfig";
import { detectVisitorCountry } from "../Geo/geoService";
import { trackEvent } from "../Analytics/analytics";

const CountryContext = createContext(null);

const SESSION_COUNTRY_KEY = "konn3ct_active_country";
const SESSION_DETECTED_KEY = "konn3ct_detected_country";
const USER_COUNTRY_KEY = "konn3ct_country_preference";

// const readStoredCountry = () => {
//   if (typeof window === "undefined") return null;

//   const routeCountry = getCountryCodeFromPath(window.location.pathname);
//   if (routeCountry) return routeCountry;

//   const userChoice = window.localStorage.getItem(USER_COUNTRY_KEY);
//   if (userChoice && COUNTRY_CONFIG[userChoice]) return userChoice;

//   const sessionChoice = window.sessionStorage.getItem(SESSION_COUNTRY_KEY);
//   if (sessionChoice && COUNTRY_CONFIG[sessionChoice]) return sessionChoice;

//   return null;
// };

const readStoredCountry = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const pathname = window.location.pathname.toLowerCase();

  // 1. Explicit URL always wins.
  // /ng -> NG
  // /ke -> KE
  // /gh -> GH
  // /global -> GLOBAL
  const routeCountry = getCountryCodeFromPath(pathname);

  if (routeCountry) {
    return routeCountry;
  }

  // 2. Manual user selection wins over IP.
  const userChoice = window.localStorage.getItem(USER_COUNTRY_KEY);

  if (userChoice && COUNTRY_CONFIG[userChoice]) {
    return userChoice;
  }

  // IMPORTANT:
  // When entering the main website,
  // force fresh IP detection.
  if (pathname === "/" || pathname === "/meet") {
    return null;
  }

  // 3. For shared pages such as
  // /events and /pricing,
  // remember the active country.
  const sessionChoice = window.sessionStorage.getItem(SESSION_COUNTRY_KEY);

  if (sessionChoice && COUNTRY_CONFIG[sessionChoice]) {
    return sessionChoice;
  }

  return null;
};

export const CountryProvider = ({ children }) => {
  const initialCountryCode = readStoredCountry();

  const [country, setCountry] = useState(initialCountryCode ? COUNTRY_CONFIG[initialCountryCode] : null);
  const [detectedCountryCode, setDetectedCountryCode] = useState(
    typeof window !== "undefined" ? window.sessionStorage.getItem(SESSION_DETECTED_KEY) : null,
  );
  const [loading, setLoading] = useState(!initialCountryCode);
  const [error, setError] = useState(null);

  const activateCountry = useCallback((countryCode, { remember = false, detectedCode = null } = {}) => {
    const normalized = normalizeCountryCode(countryCode);
    const nextCountry = COUNTRY_CONFIG[normalized];

    setCountry(nextCountry);
    setError(null);

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(SESSION_COUNTRY_KEY, normalized);

      if (detectedCode) {
        window.sessionStorage.setItem(SESSION_DETECTED_KEY, detectedCode);
        setDetectedCountryCode(detectedCode);
      }

      if (remember) {
        window.localStorage.setItem(USER_COUNTRY_KEY, normalized);
      }
    }

    return nextCountry;
  }, []);

  const selectCountry = useCallback(
    (countryCode) => activateCountry(countryCode, { remember: true }),
    [activateCountry],
  );

  const clearCountryPreference = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(USER_COUNTRY_KEY);
    }
  }, []);

  const refreshDetection = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await detectVisitorCountry();
      const normalized = normalizeCountryCode(result.detectedCountryCode);

      activateCountry(normalized, {
        detectedCode: result.detectedCountryCode,
      });

      trackEvent("country_detected", {
        detected_country: result.detectedCountryCode,
        active_country: normalized,
        suggested_currency: result.suggestedCurrency || undefined,
      });
    } catch (err) {
      console.error("Geo detection failed:", err);

      setError(err);
      activateCountry("GLOBAL");

      trackEvent("country_detection_failed", {
        message: err?.message || "Unknown geo detection error",
      });
    } finally {
      setLoading(false);
    }
  }, [activateCountry]);

  useEffect(() => {
    if (initialCountryCode) {
      setLoading(false);
      return;
    }

    refreshDetection();
  }, [initialCountryCode, refreshDetection]);

  const value = useMemo(
    () => ({
      country: country || getCountryConfig("GLOBAL"),
      detectedCountryCode,
      loading,
      error,
      activateCountry,
      selectCountry,
      clearCountryPreference,
      refreshDetection,
    }),
    [
      country,
      detectedCountryCode,
      loading,
      error,
      activateCountry,
      selectCountry,
      clearCountryPreference,
      refreshDetection,
    ],
  );

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
};

export const useCountry = () => {
  const context = useContext(CountryContext);

  if (!context) {
    throw new Error("useCountry must be used inside CountryProvider.");
  }

  return context;
};
